import { ApiError } from '../api/client';
import { DomainError, DomainErrorKind, NameField } from '../domain/errors';

/**
 * The one place a failed call becomes a domain outcome (research R8). Maps by
 * the contract's `code` first — that is what tells `nothing_new` from
 * `default_collection`, both 409 — and falls back to the status when the body
 * doesn't decode. Anything that isn't an answer from the server (a network
 * error, a timeout, a bad body) is `failure`, the only outcome worth a retry.
 */
export function toDomainError(e: unknown): DomainError {
  if (e instanceof DomainError) return e;
  if (e instanceof ApiError) return fromAnswer(e);
  const message = e instanceof Error && e.message ? e.message : 'Something went wrong.';
  return new DomainError('failure', message);
}

/** Run a call and report any failure as a `DomainError`. */
export async function mapped<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (e) {
    throw toDomainError(e);
  }
}

const BY_CODE: Record<string, DomainErrorKind> = {
  not_found: 'notFound',
  listing_deleted: 'listingDeleted',
  // Gone or hidden, alike: to every screen just a collection that isn't there.
  collection_unavailable: 'notFound',
  not_yet_available: 'notYetAvailable',
  permission_denied: 'notPermitted',
  verification_required: 'verificationRequired',
  invitation_expired: 'invitationExpired',
  nothing_new: 'nothingNew',
  default_collection: 'defaultCollection',
  count_changed: 'countChanged',
  keep_list_changed: 'keepListChanged',
  change_in_progress: 'changeInProgress',
  name_required: 'nameRequired',
  invalid: 'invalid',
  throttled: 'throttled',
  not_authenticated: 'notAuthenticated',
};

const BY_STATUS: Record<number, DomainErrorKind> = {
  400: 'invalid',
  401: 'notAuthenticated',
  403: 'notPermitted',
  404: 'notFound',
  410: 'invitationExpired',
  429: 'throttled',
  501: 'notYetAvailable',
};

function fromAnswer(e: ApiError): DomainError {
  const kind = (e.code && BY_CODE[e.code]) || BY_STATUS[e.status] || 'failure';
  const body = isRecord(e.body) ? e.body : {};
  return new DomainError(
    kind,
    e.message,
    kind === 'invalid' ? fieldsOf(body) : {},
    missingOf(body),
  );
}

/**
 * The envelope's `fields`, or a plain DRF validation body (`{field: [msg]}`),
 * keyed by the domain's camelCase names rather than the contract's.
 */
function fieldsOf(body: Record<string, unknown>): Record<string, string[]> {
  const source = isRecord(body.fields) ? body.fields : 'code' in body ? {} : body;
  const fields: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(source)) {
    if (!Array.isArray(value)) continue;
    const messages = value.filter((m): m is string => typeof m === 'string');
    if (messages.length) fields[camel(key)] = messages;
  }
  return fields;
}

function missingOf(body: Record<string, unknown>): NameField[] {
  if (!Array.isArray(body.missing)) return [];
  return body.missing
    .map((m) => (m === 'first_name' ? 'firstName' : m === 'last_name' ? 'lastName' : null))
    .filter((m): m is NameField => m !== null);
}

function camel(key: string): string {
  return key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
